import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Send } from 'lucide-react';
import { aiApi } from '@/api/ai';
import { useAppStore } from '@/app/appStore';
import { usePanelStore } from '@/panels/panelStore';
import { LAYOUT_TEMPLATE_SURFACE } from '@/templates/layoutTemplates';
import { useChatStore } from './chatStore';
import { buildDefaultSessionRequest } from './sessionDefaults';

/**
 * Shared focus signal for the chat composer.
 *
 * Incremented by the global ⌘/ shortcut so the next render of `Composer`
 * pulls focus to its textarea — a tiny pub/sub kept here to avoid rendering
 * the composer through Zustand just for this side-effect.
 */
let focusSignal = 0;
const focusSubscribers = new Set<() => void>();

export function requestFocusChatComposer(): void {
  focusSignal += 1;
  for (const sub of focusSubscribers) sub();
}

function useFocusSignal(onSignal: () => void): void {
  useEffect(() => {
    focusSubscribers.add(onSignal);
    return () => {
      focusSubscribers.delete(onSignal);
    };
  }, [onSignal]);
}

export function Composer() {
  const { t } = useTranslation('chat');
  const [draft, setDraft] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const lastFocusSignalRef = useRef(focusSignal);

  useFocusSignal(() => {
    if (lastFocusSignalRef.current === focusSignal) return;
    lastFocusSignalRef.current = focusSignal;
    textareaRef.current?.focus();
  });
  const sessionId = useChatStore((s) => s.sessionId);
  const setSession = useChatStore((s) => s.setSession);
  const append = useChatStore((s) => s.append);
  const newCorrelation = useChatStore((s) => s.newCorrelation);
  const isSending = useChatStore((s) => s.isSending);
  const setIsSending = useChatStore((s) => s.setIsSending);
  const handleUiAction = usePanelStore((s) => s.handleUiAction);
  const setLastAgentAction = useAppStore((s) => s.setLastAgentAction);
  const openLayoutSaveDialog = useAppStore((s) => s.openLayoutSaveDialog);

  async function send(content: string) {
    const trimmed = content.trim();
    if (!trimmed || isSending) return;
    setIsSending(true);

    const correlationId = newCorrelation();
    const now = new Date().toISOString();
    append({
      role: 'user',
      content: trimmed,
      timestamp: now,
      correlationId,
    });

    try {
      let sid = sessionId;
      if (!sid) {
        const session = await aiApi.createSession(buildDefaultSessionRequest(), { correlationId });
        sid = session.session_id;
        setSession(sid);
      }

      const res = await aiApi.sendMessage(
        { session_id: sid, message: trimmed },
        { correlationId },
      );

      append({
        role: 'assistant',
        content: res.response,
        timestamp: res.timestamp,
        correlationId,
        uiAction: res.ui_action,
        suggestedPrompts: res.suggested_prompts,
      });

      if (res.ui_action) {
        if (res.ui_action.type === 'save_template' && res.ui_action.surface === LAYOUT_TEMPLATE_SURFACE) {
          const suggestedName = typeof res.ui_action.payload?.['name'] === 'string'
            ? (res.ui_action.payload['name'] as string)
            : '';
          openLayoutSaveDialog(suggestedName);
        } else {
          handleUiAction(res.ui_action, correlationId);
        }
        setLastAgentAction({
          correlationId,
          description: `${res.ui_action.type}: ${res.ui_action.title}`,
          timestamp: res.timestamp,
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'send failed';
      append({
        role: 'assistant',
        content: t('sendError') + ' — ' + message,
        timestamp: new Date().toISOString(),
        correlationId,
      });
    } finally {
      setIsSending(false);
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send(draft);
      setDraft('');
    }
  }

  return (
    <div className="border-t border-border bg-background p-2">
      <div className="flex items-end gap-2">
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('placeholder')}
          rows={2}
          className="min-h-[44px] flex-1 resize-none rounded-md border border-input bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          disabled={isSending}
          aria-label={t('placeholder')}
          data-testid="chat-composer-textarea"
        />
        <button
          type="button"
          onClick={() => {
            void send(draft);
            setDraft('');
          }}
          disabled={isSending || !draft.trim()}
          aria-label={t('send')}
          className="rounded-md bg-primary p-2 text-primary-foreground disabled:opacity-50 hover:bg-primary/90"
        >
          <Send size={14} />
        </button>
      </div>
    </div>
  );
}
